import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import VideoPlayer from '../components/VideoPlayer';

const PROGRESS_KEY = 'hipaa_video_progress';

function loadProgress() {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  return { completedIndexes: [], currentIndex: 0 };
}

function saveProgress(data) {
  try { localStorage.setItem(PROGRESS_KEY, JSON.stringify(data)); } catch (_) {}
}

export default function VideoTraining({ userData, onComplete }) {
  const [videos, setVideos]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [completedIndexes, setCompletedIndexes] = useState([]);
  const [currentDone, setCurrentDone]   = useState(false);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const { data } = await axios.get(`/api/videos?role=${encodeURIComponent(userData.role)}`);
        setVideos(data.videos);
        const saved = loadProgress();
        const validIdx = Math.min(saved.currentIndex, data.videos.length - 1);
        setCurrentIndex(validIdx);
        setCompletedIndexes(saved.completedIndexes || []);
        setCurrentDone((saved.completedIndexes || []).includes(validIdx));
      } catch (err) {
        setError('Unable to load training videos. Please refresh and try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, [userData.role]);

  const handleVideoComplete = useCallback(() => {
    setCurrentDone(true);
    setCompletedIndexes((prev) => {
      const updated = prev.includes(currentIndex) ? prev : [...prev, currentIndex];
      saveProgress({ completedIndexes: updated, currentIndex });
      return updated;
    });
  }, [currentIndex]);

  const handleNext = () => {
    const nextIndex = currentIndex + 1;
    if (nextIndex >= videos.length) {
      saveProgress({ completedIndexes: [], currentIndex: 0 });
      onComplete({ completedAt: new Date().toISOString() });
      return;
    }
    setCurrentIndex(nextIndex);
    setCurrentDone(completedIndexes.includes(nextIndex));
    saveProgress({ completedIndexes, currentIndex: nextIndex });
  };

  const handleJumpTo = (idx) => {
    if (idx <= Math.max(...completedIndexes, -1) + 1) {
      setCurrentIndex(idx);
      setCurrentDone(completedIndexes.includes(idx));
    }
  };

  const totalVideos  = videos.length;
  const overallPct   = totalVideos ? (completedIndexes.length / totalVideos) * 100 : 0;
  const currentVideo = videos[currentIndex];
  const isLastVideo  = currentIndex === totalVideos - 1;
  const allDone      = completedIndexes.length === totalVideos;

  const getItemStatus = (idx) => {
    if (completedIndexes.includes(idx)) return 'done';
    if (idx === currentIndex) return 'active';
    return 'pending';
  };

  if (loading) return (
    <div className="page-container--wide">
      <div className="card" style={{ textAlign: 'center', padding: '60px 40px' }}>
        <div className="spinner" style={{ margin: '0 auto 20px' }} />
        <p style={{ color: '#546E7A' }}>Loading your training program…</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="page-container">
      <div className="card" style={{ textAlign: 'center', padding: '60px 40px' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
        <h2 style={{ color: '#1B3A5C', marginBottom: 12 }}>Connection Error</h2>
        <p style={{ color: '#546E7A', marginBottom: 24 }}>{error}</p>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>Retry</button>
      </div>
    </div>
  );

  return (
    <div className="page-container--wide">
      <div style={{ marginBottom: 20 }}>
        <div className="step-badge"><span className="step-badge-dot" />Step 2 of 4 — Video Training</div>
        <h1 className="page-title">HIPAA Compliance Training Modules</h1>
        <p className="page-subtitle">
          Welcome, <strong>{userData.fullName}</strong>. Each video must be watched in full — forward skipping is disabled.
          <span style={{ display: 'block', marginTop: 8, color: '#C53030', fontWeight: 700 }}>
            ⚠️ You must watch 100% of each video before you can proceed to the next.
          </span>
        </p>
        {process.env.NODE_ENV !== 'production' && (
          <button
            onClick={() => {
              saveProgress({ completedIndexes: [], currentIndex: 0 });
              onComplete({ completedAt: new Date().toISOString() });
            }}
            style={{
              marginTop: 10, padding: '8px 14px', border: '1.5px dashed #F0900A',
              background: '#FFF8EC', color: '#B85C00', borderRadius: 8,
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
            }}
          >
            🧪 DEV: Skip all videos → Quiz
          </button>
        )}
      </div>

      <div className="training-layout">
        <aside className="progress-sidebar">
          <div className="progress-sidebar-title">Training Progress</div>
          <ul className="video-list">
            {videos.map((v, idx) => {
              const status = getItemStatus(idx);
              const locked = idx > (Math.max(...completedIndexes, -1) + 1);
              return (
                <li key={v.id} className={`video-list-item video-list-item--${status}`}
                  onClick={() => !locked && handleJumpTo(idx)}
                  style={{ cursor: locked ? 'default' : 'pointer' }}
                  title={locked ? 'Complete previous videos first' : v.title}>
                  <div className={`video-list-icon video-list-icon--${status}`}>
                    {status === 'done' ? '✓' : status === 'active' ? '▶' : idx + 1}
                  </div>
                  <div className="video-list-meta">
                    <div className="video-list-title">{v.title}</div>
                    <div className="video-list-label">
                      {idx === totalVideos - 1 ? `Role: ${userData.role}` : 'All Roles'}
                      {locked ? ' 🔒' : ''}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="overall-progress">
            <div className="overall-progress-label">
              <span>Overall Progress</span>
              <span>{completedIndexes.length} / {totalVideos}</span>
            </div>
            <div className="overall-progress-bar">
              <div className="overall-progress-fill" style={{ width: `${overallPct}%` }} />
            </div>
          </div>

          <div style={{ marginTop: 20, padding: '14px 16px', background: '#EEF3F8', borderRadius: 7, fontSize: 12, color: '#546E7A' }}>
            <div style={{ fontWeight: 700, color: '#1B3A5C', marginBottom: 4 }}>{userData.fullName}</div>
            <div>{userData.role}</div>
            <div style={{ marginTop: 4, wordBreak: 'break-all' }}>{userData.email}</div>
          </div>
        </aside>

        <div className="video-panel">
          <div className="video-panel-header">
            <div className="video-counter">
              Video {currentIndex + 1} of {totalVideos}
              {currentIndex === totalVideos - 1 && (
                <span style={{ marginLeft: 10, background: 'rgba(0,163,163,0.12)', color: '#00A3A3', padding: '2px 10px', borderRadius: 10, fontSize: 11 }}>
                  Role-Specific
                </span>
              )}
            </div>
            <h2 className="video-title">{currentVideo?.title}</h2>
            <p className="video-desc">{currentVideo?.description}</p>
          </div>

          {currentVideo && (
            <VideoPlayer key={currentVideo.url} src={currentVideo.url}
              onComplete={handleVideoComplete} isCompleted={completedIndexes.includes(currentIndex)} />
          )}

          <div className="video-panel-footer">
            <div className={`completion-note${currentDone ? ' completion-note--done' : ''}`}>
              {currentDone ? <>✅ Video complete — you may proceed</> : <>⏳ Watch to completion to unlock Next</>}
            </div>
            <button className="btn btn-secondary" onClick={handleNext} disabled={!currentDone}>
              {allDone ? 'Finish & Get Certificate →' : isLastVideo && currentDone ? 'Finish & Get Certificate →' : 'Next Video →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
